import { useMemo } from 'react';
import {
  FavoriteResponse,
  useGetTripsFavoritesQuery,
  usePostTripsByTripIdFavoriteAndLinkSecretMutation,
} from '../../store/backendApi';

/**
 * Platform-agnostic hook for managing favorites page data.
 * Handles fetching, sorting, grouping by year, and unfavoriting trips.
 */
export function useFavoritesData() {
  const {
    data: favoritesData,
    isLoading: areFavoritesLoading,
    error: favoritesError,
  } = useGetTripsFavoritesQuery();

  const [toggleFavorite] = usePostTripsByTripIdFavoriteAndLinkSecretMutation();

  // Sort trips by start date (most recent first)
  const sortedFavorites = useMemo(() => {
    if (!favoritesData) return [];
    return [...favoritesData].sort(
      (a, b) => new Date(b.trip.startDate).getTime() - new Date(a.trip.startDate).getTime(),
    );
  }, [favoritesData]);

  // Group trips by year for display
  const favoritesByYear = useMemo(() => {
    const grouped = sortedFavorites.reduce(
      (acc, fav) => {
        const year = new Date(fav.trip.startDate).getFullYear().toString();
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(fav);
        return acc;
      },
      {} as Record<string, FavoriteResponse[]>,
    );

    return Object.keys(grouped)
      .sort((a, b) => parseInt(b) - parseInt(a)) // Most recent year first
      .reduce(
        (acc, year) => {
          acc[year] = grouped[year];
          return acc;
        },
        {} as Record<string, FavoriteResponse[]>,
      );
  }, [sortedFavorites]);

  // Handler to unfavorite a trip
  const unfavoriteTrip = async (tripId: string, linkSecret: string | null) => {
    await toggleFavorite({
      tripId: tripId,
      linkSecret: linkSecret ?? 'none',
    });
  };

  return {
    favorites: favoritesData,
    sortedFavorites,
    favoritesByYear,
    isLoading: areFavoritesLoading,
    error: favoritesError,
    unfavoriteTrip,
    hasFavorites: (sortedFavorites?.length ?? 0) > 0,
  };
}
